import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DashboardPage } from '../../dashboard/pages/DashboardPage';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import Avatar from '@mui/material/Avatar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import LinearProgress from '@mui/material/LinearProgress';
import { API_BASE_URL } from '@/config';

interface UserProfile {
  id: string;
  display_name: string;
  biography: string | null;
  country_code: string | null;
  avatar_url: string | null;
  plan: string;
  photo_quota: number;
  photos_used: number;
  default_privacy: string;
  preferred_locale: string;
  email: string;
  created_at: string;
}

export function ProfilePage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const avatarInputRef = useRef<HTMLInputElement>(null);
  const [showDeleteAccount, setShowDeleteAccount] = useState(false);
  const [deleteAccountConfirmed, setDeleteAccountConfirmed] = useState(false);
  const [deletionResult, setDeletionResult] = useState<any>(null);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('accessToken');
        const res = await fetch(`${API_BASE_URL}/api/v1/users/me`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        if (!res.ok) {
          throw new Error(`Erro ao carregar perfil (${res.status})`);
        }

        const data: UserProfile = await res.json();
        setProfile(data);
      } catch (err: unknown) {
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
        <Typography sx={{ mt: 2 }} color="text.secondary">
          {t('profile.loading')}
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
      </Container>
    );
  }

  if (!profile) return null;

  const usagePercent = Math.min(
    (profile.photos_used / profile.photo_quota) * 100,
    100
  );

  const memberSince = new Date(profile.created_at).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  });

  const planLabel = profile.plan.charAt(0).toUpperCase() + profile.plan.slice(1);

  const privacyLabel =
    profile.default_privacy === 'public'
      ? t('profile.public')
      : t('profile.private');

  const localeLabel =
    profile.preferred_locale === 'pt-BR'
      ? 'Português (Brasil)'
      : profile.preferred_locale === 'en'
        ? 'English'
        : profile.preferred_locale;

  const avatarLetter = (profile.display_name || 'U').charAt(0).toUpperCase();

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, mb: 4 }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar
            src={profile.avatar_url || undefined}
            alt={profile.display_name}
            sx={{
              width: 96,
              height: 96,
              fontSize: 40,
              bgcolor: 'primary.main',
            }}
          >
            {avatarLetter}
          </Avatar>
          <IconButton
            size="small"
            sx={{ position: 'absolute', bottom: 0, right: 0, bgcolor: 'background.paper', boxShadow: 1, '&:hover': { bgcolor: 'grey.100' } }}
            onClick={() => avatarInputRef.current?.click()}
          >
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            style={{ display: 'none' }}
            onChange={async (e) => {
              const file = e.target.files?.[0];
              if (!file) return;
              const formData = new FormData();
              formData.append('file', file);
              const token = localStorage.getItem('accessToken');
              const res = await fetch(`${API_BASE_URL}/api/v1/users/me/avatar`, {
                method: 'POST',
                headers: token ? { Authorization: `Bearer ${token}` } : {},
                body: formData,
              });
              if (res.ok) {
                const data = await res.json();
                setProfile((prev: any) => ({ ...prev, avatar_url: data.avatarUrl }));
              }
            }}
          />
        </Box>
        <Box sx={{ flex: 1 }}>
          {editingName ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                size="small"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                autoFocus
                onKeyDown={async (e) => {
                  if (e.key === 'Enter' && newName.trim()) {
                    const token = localStorage.getItem('accessToken');
                    const res = await fetch(`${API_BASE_URL}/api/v1/users/me`, {
                      method: 'PATCH',
                      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                      body: JSON.stringify({ display_name: newName.trim() }),
                    });
                    if (res.ok) {
                      setProfile((prev: any) => ({ ...prev, display_name: newName.trim() }));
                    }
                    setEditingName(false);
                  } else if (e.key === 'Escape') {
                    setEditingName(false);
                  }
                }}
              />
              <Button size="small" variant="contained" onClick={async () => {
                if (!newName.trim()) return;
                const token = localStorage.getItem('accessToken');
                const res = await fetch(`${API_BASE_URL}/api/v1/users/me`, {
                  method: 'PATCH',
                  headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
                  body: JSON.stringify({ display_name: newName.trim() }),
                });
                if (res.ok) {
                  setProfile((prev: any) => ({ ...prev, display_name: newName.trim() }));
                }
                setEditingName(false);
              }}>OK</Button>
              <Button size="small" onClick={() => setEditingName(false)}>X</Button>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="h4" fontWeight={700}>
                {profile.display_name}
              </Typography>
              <IconButton size="small" onClick={() => { setNewName(profile.display_name || ''); setEditingName(true); }}>
                <EditIcon sx={{ fontSize: 16 }} />
              </IconButton>
            </Box>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
            <Typography variant="body2" color="text.secondary">
              {profile.email}
            </Typography>
            <Button variant="outlined" size="small" onClick={() => navigate('/auth/forgot-password')}>
              {t('profile.changePassword')}
            </Button>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
            <Chip
              label={planLabel}
              size="small"
              color="primary"
              sx={{ fontWeight: 600 }}
            />
            <Button variant="outlined" size="small" onClick={() => navigate('/plans')}>
              {t('profile.changePlan')}
            </Button>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {t('profile.memberSince')} {memberSince}
          </Typography>
        </Box>
      </Box>

            {/* Armazenamento */}
      <Card sx={{ mb: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t('profile.storage')}
          </Typography>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {profile.photos_used} / {profile.photo_quota.toLocaleString('pt-BR')} {t('profile.storageDesc')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {usagePercent.toFixed(1)}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={usagePercent}
            color={usagePercent > 90 ? 'error' : 'primary'}
            sx={{ height: 10, borderRadius: 5 }}
          />
        </CardContent>
      </Card>





      {/* Dashboard */}
      <Box sx={{ mb: 2 }}>
        <DashboardPage />
      </Box>

      {/* Cancelar conta - discreto, alinhado direita */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3, mb: 2 }}>
        <Button variant="text" size="small" sx={{ fontSize: '0.75rem', color: 'text.disabled', '&:hover': { color: 'error.main' } }} onClick={() => { setShowDeleteAccount(true); setDeleteAccountConfirmed(false); setDeletionResult(null); }}>
          Cancelar minha conta
        </Button>
      </Box>

      {/* Delete Account Dialog */}
      <Dialog open={showDeleteAccount} onClose={() => setShowDeleteAccount(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>
          Excluir Conta Permanentemente
        </DialogTitle>
        <DialogContent>
          {deletionResult ? (
            <Box>
              <Alert severity="warning" sx={{ mb: 2 }}>
                Sua conta será excluída permanentemente em <strong>{deletionResult.deletionDate}</strong> ({deletionResult.gracePeriodDays} dias).
              </Alert>
              <Typography variant="body2" color="text.secondary">
                Durante este período você pode cancelar a exclusão fazendo login novamente. Após a data, todos os dados serão removidos irreversivelmente.
              </Typography>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Ao solicitar a exclusão da conta, conforme a LGPD (Art. 18):
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2, '& li': { mb: 0.5 } }}>
                <li><Typography variant="body2">Sua conta será desativada imediatamente</Typography></li>
                <li><Typography variant="body2">Você terá <strong>30 dias</strong> para reverter a decisão</Typography></li>
                <li><Typography variant="body2">Após 30 dias, todos os dados serão excluídos permanentemente:</Typography>
                  <Box component="ul" sx={{ pl: 2, mt: 0.5 }}>
                    <li><Typography variant="body2" color="error">Todas as fotos</Typography></li>
                    <li><Typography variant="body2" color="error">Todos os pins e viagens</Typography></li>
                    <li><Typography variant="body2" color="error">Perfil e dados pessoais</Typography></li>
                    <li><Typography variant="body2" color="error">Histórico de pagamentos</Typography></li>
                  </Box>
                </li>
              </Box>
              <FormControlLabel
                control={<Checkbox checked={deleteAccountConfirmed} onChange={(e) => setDeleteAccountConfirmed(e.target.checked)} color="error" />}
                label={<Typography variant="body2">Confirmo que desejo excluir minha conta e todos os meus dados permanentemente</Typography>}
              />
            </Box>
          )}
        </DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, p: 2, pt: 0 }}>
          <Button onClick={() => setShowDeleteAccount(false)} variant="outlined" size="small">
            {deletionResult ? 'Fechar' : 'Cancelar'}
          </Button>
          {!deletionResult && (
            <Button
              variant="contained"
              color="error"
              size="small"
              disabled={!deleteAccountConfirmed}
              onClick={async () => {
                const token = localStorage.getItem('accessToken');
                const res = await fetch(API_BASE_URL + '/api/v1/users/me/account', { method: 'DELETE', headers: token ? { Authorization: 'Bearer ' + token } : {} });
                if (res.ok) {
                  const data = await res.json();
                  setDeletionResult(data);
                }
              }}
            >
              Excluir Minha Conta
            </Button>
          )}
        </Box>
      </Dialog>

    </Container>
  );
}